import { PhoneInput } from '@velocity/ui'
import { useMemo } from 'react'

import { useContent, useLocalization } from '@ngb-frontend/shared/context'
import { validation as v } from '@ngb-frontend/shared/utils'

import { FormField } from '../FormField/FormField'

import type { FormFieldWrapper } from '@ngb-frontend/shared/types'
import type { CountryCode } from 'libphonenumber-js'

export const PhoneField: React.FC<Omit<FormFieldWrapper, 'placeholder'>> = ({
  classes,
  name = 'phone',
  label,
  hideLabel,
  disabled,
  noMargin,
  noValidation,
}) => {
  const c = useContent()
  const localeCountry = useLocalization().locale.split('-')[1]

  const validators = useMemo(() => {
    const phoneValidators =
      localeCountry !== 'GLOBAL'
        ? [
            v.apply<string>(v.isPhoneTooLong(localeCountry as CountryCode), {
              message: c('errors.phoneTooLong'),
              type: 'errorType',
            }),
            v.apply<string>(v.isPhoneTooShort(localeCountry as CountryCode), {
              message: c('errors.phoneTooShort'),
              type: 'errorType',
            }),
            v.apply<string>(
              v.isValidPhoneNumber(localeCountry as CountryCode),
              {
                message: c('errors.invalidPhone'),
                type: 'errorType',
              },
            ),
          ]
        : []

    return v.compose(
      v.apply(v.required, {
        message: c('errors.required'),
        type: 'errorType',
      }),
      ...phoneValidators,
    )
  }, [c, localeCountry])

  return (
    <FormField<string>
      name={name}
      id={`${name}-input`}
      label={label || c('fields.phone.label')}
      hideLabel={hideLabel}
      validator={validators}
      noValidation={noValidation}
      classes={classes}
      noMargin={noMargin}
      disabled={!!disabled}
    >
      {({ input }) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { onFocus, onBlur, ...rest } = input

        return (
          <PhoneInput
            {...rest}
            inputProps={{
              'data-e2e-component': `${name}-input`,
            }}
            required={false}
            disabled={!!disabled}
          />
        )
      }}
    </FormField>
  )
}
